import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";

/**
 * Resolution states for the roles claim on the access token. `loading` is the
 * initial, pre-resolution state; `error` is the terminal state used when the
 * token cannot be fetched or decoded, or does not arrive within
 * `ROLES_TIMEOUT_MS`.
 *
 * A gate must treat `error` as "unknown", never as "no roles": an
 * administrator whose token refresh failed is still an administrator, and
 * reading the failure as an empty role list silently demotes them.
 */
export type RolesStatus = "loading" | "ready" | "error";

/**
 * Fail-closed timeout (ms) for resolving the access token. Mirrors
 * `STAFF_AUTH_TIMEOUT_MS` in `admin/useStaffAuthorization.ts`: without it a
 * token promise that never settles leaves every roles gate spinning with no
 * error and no way out.
 */
export const ROLES_TIMEOUT_MS = 10000;

interface RolesClaim {
  user_roles?: string[];
}

export interface RolesResolution {
  status: RolesStatus;
  roles: string[];
}

/**
 * Resolves the `user_roles` claim on the current access token, and reports
 * whether it resolved at all, so a caller gating on a role can:
 *   - show a loading indicator until resolution (`loading`),
 *   - read the roles (`ready`),
 *   - report a failure and stop (`error`) — never hang, never demote.
 */
export function useRolesResolution(): RolesResolution {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } =
    useAuth0();
  const [resolution, setResolution] = useState<RolesResolution>({
    status: "loading",
    roles: [],
  });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      setResolution({ status: "ready", roles: [] });
      return;
    }

    let settled = false;
    const settle = (next: RolesResolution) => {
      if (!settled) {
        settled = true;
        setResolution(next);
      }
    };

    const timeout = setTimeout(
      () => settle({ status: "error", roles: [] }),
      ROLES_TIMEOUT_MS,
    );

    getAccessTokenSilently()
      .then((token) => {
        // A token issued without the claim decodes to undefined, and every
        // caller reads .includes() or .length off this list.
        settle({
          status: "ready",
          roles: jwtDecode<RolesClaim>(token).user_roles ?? [],
        });
      })
      .catch(() => {
        // A malformed token throws out of jwtDecode into here as well.
        settle({ status: "error", roles: [] });
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      settled = true;
      clearTimeout(timeout);
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading, user]);

  return resolution;
}

/**
 * The roles on the current access token — `[]` until they resolve, and `[]`
 * if resolving them fails. A caller that has to tell those two apart, such as
 * an administrator-only gate, wants `useRolesResolution()` instead.
 */
export function useRoles() {
  return useRolesResolution().roles;
}
